// ExperimentForm.tsx — the create / fork / edit dialog behind the
// experiments pane's action buttons.
//
// Three modes, one form, because the fields are nearly the same object:
//   create — POST   /experiments            (slug, title, hypothesis, branch,
//                                            replicate_target, objectives)
//   fork   — POST   /experiments/{parent}/fork  (same, parent implied)
//   edit   — PATCH  /experiments/{slug}     (slug, title, hypothesis, status,
//                                            replicate_target)
//            plus PUT  /experiments/{slug}/objectives when the objective
//            list changed, and POST /experiments/{slug}/reparent when the
//            parent changed — the API keeps those separate, so the dialog
//            sends up to three requests and reports the first failure.
//
// Edit order is deliberate: reparent, then patch, then objectives. The move
// is the one request the server can refuse for a reason the form cannot
// predict (a cycle, 409), so it goes first and a refusal leaves the
// experiment entirely untouched. Anything that did land before a later
// failure is remembered in `applied`, so closing the dialog still reloads
// the pane onto the current name rather than leaving it pointing at a slug
// that no longer exists.
//
// Renaming changes the slug and nothing else: the experiment keeps its id,
// its branch bindings, its values and its lineage. The pane's URL carries
// the slug, so the saved experiment's *server-returned* slug is what gets
// handed back — never the text in the field, which may have been rejected.
//
// Branch and metric suggestions come from /branches and /metrics; both are
// decoration, so a 404 on either leaves a dialog that still submits.
import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { BsSave } from 'react-icons/bs';
import { StatusBadge, unwrapJson, ui } from 'common';
import { apiClient } from '../api';
import type { Experiment, ExperimentStatus, MetricDef } from '../api';
import { parentOptions, slugError } from './experimentEdit';

export type ExperimentFormMode = 'create' | 'fork' | 'edit';

interface Props {
  mode: ExperimentFormMode;
  owner: string;
  name: string;
  /** The experiment being edited (edit) or forked from (fork). */
  subject: Experiment | null;
  /** Every experiment in the repo — the parent picker's candidate set. */
  experiments: Experiment[];
  onClose: () => void;
  /** Called with the saved experiment so the pane can reload and select it. */
  onSaved: (saved: Experiment | null) => void;
}

const STATUS_OPTIONS: { value: ExperimentStatus; label: string }[] = [
  { value: 'active', label: 'active' },
  { value: 'consolidated', label: 'consolidated' },
  { value: 'abandoned', label: 'abandoned' },
];

const ROOT_OPTION = { value: '', label: '— none (a root)' };

// Branch names carry slashes and capitals; slugs carry neither. Mirrors the
// server's SlugifyBranch so the suggested slug matches what the ingest path
// would have created on its own.
function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function ExperimentForm({
  mode, owner, name, subject, experiments, onClose, onSaved,
}: Props) {
  const editing = mode === 'edit';
  const [slug, setSlug] = useState(editing ? subject?.slug ?? '' : '');
  const [slugTouched, setSlugTouched] = useState(false);
  // The server's own objection to the name (409), cleared on the next edit
  // of the field so a stale "taken" doesn't outlive the name it was about.
  const [slugTaken, setSlugTaken] = useState('');
  const [title, setTitle] = useState(editing ? subject?.title ?? '' : '');
  const [hypothesis, setHypothesis] = useState(editing ? subject?.hypothesis ?? '' : '');
  const [branch, setBranch] = useState('');
  const [replicate, setReplicate] = useState(editing ? subject?.replicate_target ?? '' : '');
  const [status, setStatus] = useState<ExperimentStatus>(subject?.status ?? 'active');
  const [parentId, setParentId] = useState(
    editing && subject?.parent_id ? String(subject.parent_id) : '',
  );
  const [parentError, setParentError] = useState('');
  const [objectives, setObjectives] = useState<string[]>(
    (mode === 'edit' || mode === 'fork'
      ? (subject?.objectives ?? []).map((o) => o.name)
      : []),
  );
  const [objectiveDraft, setObjectiveDraft] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [metricNames, setMetricNames] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Set once a request has changed something on the server, so a later
  // failure in the same save still reloads the pane when the dialog closes.
  const [applied, setApplied] = useState<Experiment | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/branches`);
        const body = await unwrapJson<{ branches: string[] | null }>(res);
        if (!cancelled) setBranches(body.branches ?? []);
      } catch {
        if (!cancelled) setBranches([]);
      }
      try {
        const res = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/metrics`);
        const body = await unwrapJson<{ metrics: MetricDef[] | null }>(res);
        if (!cancelled) setMetricNames((body.metrics ?? []).map((d) => d.name));
      } catch {
        if (!cancelled) setMetricNames([]);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, name]);

  // Typing a branch fills the slug and the title the way the ingest path
  // would, until the user edits the slug themselves.
  const pickBranch = (b: string) => {
    setBranch(b);
    if (!slugTouched && !editing) {
      setSlug(slugify(b));
      if (!title) setTitle(b);
    }
  };

  const editSlug = (next: string) => {
    setSlugTouched(true);
    setSlugTaken('');
    setSlug(next);
  };

  const addObjective = (n: string) => {
    const want = n.trim();
    setObjectiveDraft('');
    if (!want || objectives.includes(want)) return;
    setObjectives([...objectives, want]);
  };

  const expPath = (s: string, suffix = '') =>
    `/api/v1/repos/${owner}/${name}/experiments/${encodeURIComponent(s)}${suffix}`;

  // A rename is live the moment the slug differs; an empty field in create
  // mode means "derive it from the branch", which is the server's job.
  const wantSlug = slug.trim();
  const renaming = editing && wantSlug !== '' && wantSlug !== (subject?.slug ?? '');
  const localSlugError = editing || wantSlug !== '' ? slugError(slug) : '';
  const shownSlugError = slugTaken || localSlugError;

  const candidates = editing && subject ? parentOptions(experiments, subject) : [];
  const parentChoices = [
    ROOT_OPTION,
    ...candidates.map((e) => ({ value: String(e.id), label: e.slug })),
  ];
  // A parent that is no longer offered (it became a descendant, or it left
  // the list) must not silently read as "make this a root".
  const parentValue = parentChoices.some((o) => o.value === parentId) ? parentId : '';
  const currentParent = subject?.parent_id ? String(subject.parent_id) : '';
  const moving = editing && parentValue !== currentParent;

  // PATCH / POST reparent both answer 409 for a conflict the field owns.
  // Reading the status before unwrapJson is what lets the dialog say which
  // field is at fault instead of printing the server's sentence at the top.
  const saveEdit = async (): Promise<Experiment | null> => {
    let saved: Experiment | null = null;
    let slugNow = subject!.slug;

    if (moving) {
      const res = await apiClient.fetch(expPath(slugNow, '/reparent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_id: parentValue === '' ? null : Number(parentValue) }),
      });
      if (res.status === 409) {
        setParentError(
          'That move would put the experiment inside its own lineage. Pick a different parent.',
        );
        return null;
      }
      saved = await unwrapJson<Experiment>(res);
      setApplied(saved);
    }

    const res = await apiClient.fetch(expPath(slugNow), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title,
        hypothesis,
        status,
        replicate_target: replicate,
        // Sent only on a rename: a server that checks the name for
        // uniqueness should never be asked to check it against itself.
        ...(renaming ? { slug: wantSlug } : {}),
      }),
    });
    if (res.status === 409) {
      setSlugTaken('That name is taken — pick another.');
      return null;
    }
    saved = await unwrapJson<Experiment>(res);
    // The server's answer is the authority on the name: a build that does
    // not support renaming yet returns the old slug, and the pane must
    // follow that rather than the text in the field.
    slugNow = saved?.slug || slugNow;
    setApplied(saved);

    const before = (subject?.objectives ?? []).map((o) => o.name).join(',');
    if (before !== objectives.join(',')) {
      const objRes = await apiClient.fetch(expPath(slugNow, '/objectives'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ objectives }),
      });
      await unwrapJson<unknown>(objRes);
    }
    return saved;
  };

  const saveNew = async (): Promise<Experiment | null> => {
    const path = mode === 'fork'
      ? expPath(subject!.slug, '/fork')
      : `/api/v1/repos/${owner}/${name}/experiments`;
    const res = await apiClient.fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug: wantSlug || slugify(branch),
        title: title || branch,
        hypothesis,
        branch,
        replicate_target: replicate,
        objectives,
      }),
    });
    if (res.status === 409) {
      setSlugTaken('That name is taken — pick another.');
      return null;
    }
    return unwrapJson<Experiment>(res);
  };

  const save = async () => {
    setError('');
    setParentError('');
    setSlugTaken('');
    setSaving(true);
    try {
      const saved = editing ? await saveEdit() : await saveNew();
      if (saved) onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save experiment');
    } finally {
      setSaving(false);
    }
  };

  // Closing after a partial save still has to reload the pane: something
  // changed, and the slug in the URL may be the old one.
  const close = () => {
    if (applied) onSaved(applied);
    else onClose();
  };

  const heading = editing
    ? 'Edit Experiment'
    : mode === 'fork'
      ? `Fork ${subject?.slug ?? ''}`
      : 'New Experiment';
  const canSave = !shownSlugError
    && (editing || Boolean(branch.trim() && (wantSlug || branch.trim())));

  return (
    <Modal show onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5 m-0">{heading}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="d-flex flex-column gap-3">
          {!editing && (
            <label className="d-flex flex-column gap-1">
              <span className="text-secondary">Branch</span>
              <ui.Combobox
                value={branch}
                onChange={pickBranch}
                options={branches}
                placeholder="feature/my-idea"
                style={{ width: '100%' }}
              />
              <span className="text-secondary" style={{ fontSize: '0.8em' }}>
                A branch belongs to exactly one experiment — a branch already
                taken comes back as a conflict.
              </span>
            </label>
          )}
          <label className="d-flex flex-column gap-1">
            <span className="text-secondary">Slug</span>
            <ui.Text
              value={slug}
              placeholder="my-idea"
              aria-invalid={shownSlugError ? true : undefined}
              onChange={(e) => editSlug(e.target.value)}
              style={{ width: '100%' }}
            />
            {shownSlugError ? (
              <span className="text-danger" style={{ fontSize: '0.8em' }}>
                {shownSlugError}
              </span>
            ) : editing ? (
              <span className="text-secondary" style={{ fontSize: '0.8em' }}>
                The name in links and in the URL. Renaming changes it
                everywhere; the experiment keeps its branches and its results.
              </span>
            ) : null}
          </label>
          <label className="d-flex flex-column gap-1">
            <span className="text-secondary">Title</span>
            <ui.Text
              value={title}
              placeholder="What this effort is called"
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: '100%' }}
            />
          </label>
          <label className="d-flex flex-column gap-1">
            <span className="text-secondary">Hypothesis</span>
            <textarea
              className="form-control"
              rows={3}
              value={hypothesis}
              placeholder="What this experiment is testing, and what would falsify it"
              onChange={(e) => setHypothesis(e.target.value)}
            />
          </label>
          <label className="d-flex flex-column gap-1">
            <span className="text-secondary">Replicate target</span>
            <ui.Text
              value={replicate}
              placeholder="//kaggle/rsna:train"
              onChange={(e) => setReplicate(e.target.value)}
              style={{ width: '100%' }}
            />
          </label>
          <div className="d-flex flex-column gap-1">
            <span className="text-secondary">
              Objectives — the metrics this experiment optimizes, in order.
              The first one headlines every graph row.
            </span>
            <div className="d-flex flex-wrap align-items-center gap-2">
              {objectives.map((n, i) => (
                <StatusBadge
                  key={n}
                  tone="neutral"
                  uppercase={false}
                  className="d-inline-flex align-items-center gap-1"
                >
                  <code style={{ color: 'inherit' }}>{i === 0 ? `★ ${n}` : n}</code>
                  <button
                    type="button"
                    aria-label={`Remove ${n}`}
                    className="btn btn-sm p-0 ms-1"
                    style={{ lineHeight: 1, color: 'inherit', background: 'none', border: 'none' }}
                    onClick={() => setObjectives(objectives.filter((o) => o !== n))}
                  >
                    ×
                  </button>
                </StatusBadge>
              ))}
            </div>
            <ui.Combobox
              value={objectiveDraft}
              onChange={(next) => (metricNames.includes(next) ? addObjective(next) : setObjectiveDraft(next))}
              options={metricNames.filter((n) => !objectives.includes(n))}
              placeholder="metric name — enter to add"
              onKeyDown={(e) => {
                // Enter adds what was typed — but only when the popover has
                // nothing to commit. Letting the Combobox win when it has a
                // highlighted suggestion is what keeps a half-typed metric
                // name from creating a near-miss metric def on the server.
                if (e.key !== 'Enter') return;
                const q = objectiveDraft.trim().toLowerCase();
                const suggested = q.length > 0 && metricNames.some(
                  (n) => !objectives.includes(n) && n.toLowerCase().includes(q),
                );
                if (suggested) return;
                e.preventDefault();
                addObjective(objectiveDraft);
              }}
              style={{ width: '100%' }}
            />
          </div>
          {editing && (
            <label className="d-flex flex-column gap-1">
              <span className="text-secondary">Parent</span>
              <ui.Dropdown
                value={parentValue}
                minWidth={18}
                options={parentChoices}
                onChange={(e) => { setParentError(''); setParentId(e.target.value); }}
              />
              {parentError ? (
                <span className="text-danger" style={{ fontSize: '0.8em' }}>
                  {parentError}
                </span>
              ) : (
                <span className="text-secondary" style={{ fontSize: '0.8em' }}>
                  Where this sits in the lineage. Its own descendants are not
                  offered — an experiment cannot be moved inside itself.
                </span>
              )}
            </label>
          )}
          {editing && (
            <label className="d-flex flex-column gap-1">
              <span className="text-secondary">Status</span>
              <ui.Dropdown
                value={status}
                minWidth={14}
                options={STATUS_OPTIONS}
                onChange={(e) => setStatus(e.target.value as ExperimentStatus)}
              />
            </label>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={close}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving || !canSave}>
          <BsSave className="me-1" /> Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
